import { Check, Gift, Layers, Package, Tag } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { DateTimeInput } from '@/components/ui/date-time-input'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { BranchModel, type BranchOption } from '@/http/models/Branch'
import { cn } from '@/lib/utils'
import { OFFER_TYPE_GUIDE } from '@iws/theme-schema/campaign'

import { OfferModel } from '@/types'

import { WizardStep1 } from '@/hooks/features/campaigns'

interface Props {
  values: WizardStep1
  onChange: (values: WizardStep1) => void
  onTypeChange: (type: OfferModel.OfferType) => void
}

const OFFER_TYPE_OPTIONS: {
  value: OfferModel.OfferType
  icon: typeof Tag
}[] = [
  { value: 'single_product', icon: Tag },
  { value: 'buy_x_get_y', icon: Gift },
  { value: 'volume_discount', icon: Layers },
  { value: 'combo_fixed_price', icon: Package },
]

/** ISO instant -> the value a datetime-local input shows, in the merchant's own time zone. */
const toLocalInput = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

const APPLIES_TO_OPTIONS: { value: OfferModel.AppliesTo; label: string }[] = [
  { value: 'both', label: 'Online store and POS' },
  { value: 'online', label: 'Online store only' },
  { value: 'pos', label: 'POS only' },
]

export const Step1Details = ({ values, onChange, onTypeChange }: Props) => {
  const update = (patch: Partial<WizardStep1>) => onChange({ ...values, ...patch })
  // Channel choice only matters to businesses with the POS (POS.md §18.4, checklist C29).
  const hasPos = useFeatureFlags().isFeatureEnabled('pos')
  const [branches, setBranches] = useState<BranchOption[]>([])
  const appliesTo = values.applies_to ?? 'both'
  const canListBranches = useAuth().can('branches:list')

  useEffect(() => {
    if (!hasPos || !canListBranches) return
    BranchModel.getOptions().then((res) => res.success && res.data && setBranches(res.data.result))
  }, [hasPos, canListBranches])

  return (
    <div className="space-y-4">
      {/* Offer type selector */}
      <div className="space-y-2">
        <Label>Offer type</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {OFFER_TYPE_OPTIONS.map((opt) => {
            const selected = values.type === opt.value
            const Icon = opt.icon
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => onTypeChange(opt.value)}
                className={cn(
                  'group relative flex items-start gap-3 rounded-lg border p-3 pr-9 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                  selected
                    ? 'border-primary bg-selected'
                    : 'border-border hover:bg-subdued',
                )}
              >
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
                    selected ? 'bg-primary text-primary-foreground' : 'bg-fill text-text-2 group-hover:text-ink',
                  )}
                >
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">{OFFER_TYPE_GUIDE[opt.value].label}</span>
                  <span className="mt-0.5 block text-sm text-text-2">{OFFER_TYPE_GUIDE[opt.value].summary}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">e.g. “{OFFER_TYPE_GUIDE[opt.value].example}”</span>
                </span>
                {selected && (
                  <span className="absolute right-3 top-3 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="size-3" />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="border-t border-border-subtle" />

      {/* Basic fields */}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Campaign name *</Label>
          <Input
            value={values.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="e.g. Summer sale 2025"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Start date *</Label>
            <DateTimeInput
              value={values.starts_at ? toLocalInput(values.starts_at) : ''}
              onChange={(v) => update({ starts_at: v ? new Date(v).toISOString() : '' })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>End date *</Label>
            <DateTimeInput
              value={values.ends_at ? toLocalInput(values.ends_at) : ''}
              onChange={(v) => update({ ends_at: v ? new Date(v).toISOString() : '' })}
            />
          </div>
          <p className="-mt-2 text-sm text-text-2 sm:col-span-2">
            Times are in your time zone ({TIME_ZONE}). Shoppers see the offer start and end at the same moment wherever they are.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Minimum order value</Label>
            <Input
              type="number"
              min={0}
              value={values.min_order_value || ''}
              onChange={(e) => update({ min_order_value: Number(e.target.value) })}
              placeholder="0"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Maximum total uses</Label>
            <Input
              type="number"
              min={1}
              value={values.max_total_uses ?? ''}
              onChange={(e) => update({ max_total_uses: e.target.value ? Number(e.target.value) : null })}
              placeholder="Unlimited"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Maximum uses per customer</Label>
            <Input
              type="number"
              min={1}
              value={values.max_uses_per_customer ?? ''}
              onChange={(e) => update({ max_uses_per_customer: e.target.value ? Number(e.target.value) : null })}
              placeholder="Unlimited"
            />
          </div>
        </div>

        {hasPos && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="offer-applies-to">Where it applies</Label>
              <Select value={appliesTo} onValueChange={(v) => update({ applies_to: v as OfferModel.AppliesTo })}>
                <SelectTrigger id="offer-applies-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPLIES_TO_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {appliesTo !== 'online' && branches.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="offer-branch">Branch</Label>
                <Select value={values.branch_id ?? 'all'} onValueChange={(v) => update({ branch_id: v === 'all' ? null : v })}>
                  <SelectTrigger id="offer-branch">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Every branch</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {appliesTo === 'pos' && (
              <p className="-mt-2 text-sm text-text-2 sm:col-span-2">
                POS-only offers have no campaign page in the online store. Find them in Campaigns with the channel filter set to POS only.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <Switch
            checked={values.coupon_allowed}
            onCheckedChange={(v) => update({ coupon_allowed: v })}
          />
          <Label>Customers can also use a coupon with this offer</Label>
        </div>
      </div>
    </div>
  )
}
